import { Container } from '@awazevr/design-system.components.container'
import {
  breakpoints,
  pxToRems,
  useWindowWidth,
} from '@awazevr/design-system.utils.react'
import BlogCardWithTags from '@/components/BlogCardWIthTags/BlogCardWithTags'
import styled from 'styled-components'
import {
  BaseGrid,
  BaseGridItem,
} from '@awazevr/design-system.components.base-grid'
import { TaggedArticlesLayoutOption } from './mapTaggedArticles'
import {
  Carousel,
  CarouselProps,
} from '@awazevr/design-system.components.carousel'
import { Headline } from '@awazevr/design-system.components.text'
import { Button } from '@awazevr/design-system.components.button'
import { ConditionalWrapper } from '@/components/ConditionalWrapper/ConditionalWrapper'
import { useContext, useMemo } from 'react'
import { BlogTag } from '@/components/BlogTags/mapBlogTag'
import { useIntl } from 'react-intl'
import { convertLinkToAbsoluteUrl } from '../../utils/helpers/helpers'
import { GlobalProps } from '../../utils/globalProps'

export interface BlogTaggedArticle {
  tags: BlogTag[]
  title: string
  description: string
  imageUrl: string
  imageAltText?: string
  link: string
}

export interface TaggedArticlesModuleProps {
  blogTaggedPages: BlogTaggedArticle[]
  carouselTitle?: string
  slug?: string
  layoutOption: TaggedArticlesLayoutOption
}

const BlogCardGridItem = styled(BaseGridItem)`
  justify-content: space-between;
  display: flex;
  align-items: end;
  flex-wrap: wrap;

  > .carousel-container {
    margin: ${pxToRems(24)} 0 ${pxToRems(16)};
    width: 100%;
  }
  .view-more-button {
    @media (max-width: ${breakpoints[0]}px) {
      order: 1;
      width: 100%;
    }
  }

  > .articles-container {
    width: 100%;
    @media (min-width: ${breakpoints[0]}px) {
      margin-bottom: ${pxToRems(24)};
      display: flex;
      flex-wrap: wrap;
      gap: ${pxToRems(32)} ${pxToRems(16)};

      > div {
        width: calc((1 / 3 * 100%) - (32px / 3));
        &:nth-child(-n + 2) {
          width: calc(50% - 8px);
        }
      }
    }
  }
`

const BlogCardContainer = styled.div`
  @media (min-width: ${breakpoints[0]}px) {
    display: grid;
    gap: 16px;
    grid-template-columns: repeat(3, 1fr);
  }
`

export const TaggedArticlesModule: React.FC<TaggedArticlesModuleProps> = ({
  blogTaggedPages,
  layoutOption,
  carouselTitle,
  slug,
}) => {
  const { host } = useContext(GlobalProps)
  const width = useWindowWidth()
  const isMobile: boolean = width < breakpoints[0]
  const blogChunksOfThree = useMemo(() => {
    if (isMobile) {
      return [blogTaggedPages.slice(0, 3)] // Return only the first 3 articles
    } else {
      return Array.from(
        { length: Math.ceil(blogTaggedPages.length / 3) },
        (_, i) => blogTaggedPages.slice(i * 3, i * 3 + 3),
      )
    }
  }, [blogTaggedPages, isMobile])

  const carouselProps = {
    navButtonBottomAligned: true,
    navVariant: 'outline',
    translations: {
      previousNavigation: 'Previous slide',
      nextNavigation: 'Next slide',
    },
    loop: false,
    colourScheme: 'brand-primary',
    swiperOptions: {
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 16,
      controller: {
        // Unique controller ID to control carousels individually.
        control: btoa(slug).slice(0, -2),
      },
    },
  } as CarouselProps

  const { formatMessage } = useIntl()

  return (
    <div style={{ padding: '24px 16px' }}>
      <Container>
        <BaseGrid flush>
          <BlogCardGridItem>
            {layoutOption === TaggedArticlesLayoutOption.SHOW_ALL ? (
              <div className="articles-container">
                {blogTaggedPages.map((article, index) => (
                  <BlogCardWithTags key={article.title + index} {...article} />
                ))}
              </div>
            ) : (
              <>
                <Headline as="h3" sizes={200}>
                  {carouselTitle}
                </Headline>
                {slug && (
                  <Button
                    as={'a'}
                    href={convertLinkToAbsoluteUrl(slug, host)}
                    className="view-more-button"
                    text={formatMessage({
                      id: 'blogCarousel.viewMoreArticles',
                      defaultMessage: 'View more articles',
                    })}
                    variant="solid"
                    colourScheme="brand-primary"
                  />
                )}
                <div className="carousel-container">
                  <ConditionalWrapper
                    condition={!isMobile}
                    props={carouselProps}
                    Wrapper={Carousel}
                  >
                    {blogChunksOfThree.map((chunk, index) => (
                      <BlogCardContainer key={chunk[index]?.title + index}>
                        {chunk.map((blogTaggedPage, i) => (
                          <BlogCardWithTags
                            key={blogTaggedPage?.title + i}
                            {...blogTaggedPage}
                          />
                        ))}
                      </BlogCardContainer>
                    ))}
                  </ConditionalWrapper>
                </div>
              </>
            )}
          </BlogCardGridItem>
        </BaseGrid>
      </Container>
    </div>
  )
}
